import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { NavigationTab, User } from '../../types';
import NameWithEmailTooltip from '../ui/NameWithEmailTooltip';

import logoSvg from '../../assets/logo.svg';
import helpSvg from '../../assets/help_icon.svg';

interface HeaderProps {
  user: User;
  onTabChange: (tab: NavigationTab) => void;
}

const Header: React.FC<HeaderProps> = ({ user, onTabChange }) => {
  const navigate = useNavigate();
  
  // Use name_of_employee as fallback for name
  const displayName = user?.name || user?.name_of_employee || 'User';

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-3 sm:px-4 lg:px-6 py-2.5 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Logo and Title */}
          <div className="flex min-w-0 items-center space-x-2 sm:space-x-3">
          {/* Gentari Logo */}
            <div className="flex shrink-0 items-center">
            <img src={logoSvg} alt="Gentari Logo" className="h-9 w-12 object-contain sm:h-12 sm:w-16" />
            </div>
          
          {/* Title - Hidden on mobile, shown on tablet+ */}
          <div className="hidden md:block text-gray-600 text-sm lg:text-base">
            <span className="font-medium">Employee EPM</span>
            <span className="mx-1 lg:mx-2">|</span>
            <span className="hidden md:inline">360 Feedback Portal</span>
          </div>
        </div>

        {/* Right side - Icons and User Info */}
        <div className="flex shrink-0 items-center space-x-1 sm:space-x-3 lg:space-x-4">
          {/* Notification Icon - Hidden on small mobile */}
          {/* <button className="hidden xs:block p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 touch-target">
            <img src={notificationSvg} alt="Notification Icon" className="w-6 h-6" />
          </button> */}

          {/* Help/Info Icon - Hidden on small mobile */}
          <button className="hidden sm:block p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 touch-target"
          
            onClick={() => {
          console.log('Navigating to FAQ page');
          navigate('/faq');
            }}
          >
            <img src={helpSvg} alt="Help Icon" className="w-6 h-6" />
          </button>

          {/* User Info */}
          <div className="flex items-center space-x-3 sm:space-x-4  border-gray-200 pl-3 sm:pl-4">
            {/* User name - Hidden on mobile, shown on tablet+ */}
          <div className="hidden sm:block text-right">
            <div className="text-sm font-medium text-gray-900 leading-tight">
              Hey,
              {' '}
              <NameWithEmailTooltip
                as="span"
                className="text-purple-800"
                name={displayName}
                email={user?.email}
              />
            </div>
              <div className="text-xs text-gray-500 mt-0.5">
          Welcome back
              </div>
            </div>
            
            {/* User Avatar */}
            <div 
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-r from-purple-600 to-purple-800 flex items-center justify-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 touch-target ring-2 ring-white shadow-sm" 
              title={user?.email || undefined}
              onClick={() => {
          console.log('Navigating to profile screen for user:', user);
          navigate(`/profile/${user.employee_id}`);
          onTabChange('profile');
              }}
            >
              {user?.avatar ? (
          <img 
            src={user.avatar} 
            alt={displayName}
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover"
          />
              ) : (
          <span className="text-white font-semibold text-sm sm:text-base">
            {(() => {
              const nameParts = displayName ? displayName.trim().split(/\s+/) : ['U'];
              if (nameParts.length === 1) {
                return nameParts[0][0].toUpperCase();
              }
              const firstInitial = nameParts[0][0];
              const lastInitial = nameParts[nameParts.length - 1][0];
              return (firstInitial + lastInitial).toUpperCase();
            })()}
          </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
